import { useState, useEffect, useCallback } from 'react';
import { ChangePasswordModal } from '@/modules/auth/presentation/components/ChangePasswordModal';
import { ChangeEmailModal } from '@/modules/auth/presentation/components/ChangeEmailModal';
import { LoginMethodsCard } from '@/modules/auth/presentation/components/LoginMethodsCard';
import { authContainer } from '@/modules/auth/di/container';
import type { MeResponse } from '@/modules/auth/domain/entities/auth.entity';

export function SecurityPage() {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);

  const loadMe = useCallback(() => {
    void authContainer.repository
      .getMe()
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  useEffect(() => loadMe(), [loadMe]);

  const cancelPending = useCallback(() => {
    void authContainer.repository.cancelEmailChange().then(loadMe);
  }, [loadMe]);

  const emailDescription = me?.pendingEmail
    ? `Aguardando confirmação em ${me.pendingEmail}.`
    : me?.email
      ? `Seu e-mail atual é ${me.email}.`
      : 'Atualize o endereço de e-mail associado à sua conta.';

  return (
    <div className="max-w-240 mx-auto space-y-6">
      <div>
        <h1 className="text-(--color-text-primary) text-2xl font-bold">
          Segurança da Conta
        </h1>
        <p className="text-(--color-text-secondary) mt-1 text-sm">
          Gerencie acessos e credenciais
        </p>
      </div>

      <div className="space-y-4">
        <SecurityCard
          icon={<EmailIcon />}
          title="Alterar e-mail"
          description={emailDescription}
          actionLabel={me?.pendingEmail ? 'Cancelar' : 'Alterar'}
          onAction={
            me?.pendingEmail ? cancelPending : () => setChangeEmailOpen(true)
          }
        />

        <SecurityCard
          icon={<KeyIcon />}
          title="Trocar senha"
          description="Altere a senha de acesso à sua conta."
          actionLabel="Trocar"
          onAction={() => setChangePasswordOpen(true)}
        />

        <LoginMethodsCard methods={me?.authMethods ?? []} onChanged={setMe} />
      </div>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <ChangeEmailModal
        open={changeEmailOpen}
        onClose={() => setChangeEmailOpen(false)}
        currentEmail={me?.email ?? ''}
        onStarted={loadMe}
      />
    </div>
  );
}

interface SecurityCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
}

function SecurityCard({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: SecurityCardProps) {
  return (
    <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-6">
      <div className="flex items-start gap-4">
        <div className="bg-(--color-accent)/10 text-(--color-accent) flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
          {icon}
        </div>
        <div className="flex-1">
          <h2 className="text-(--color-text-primary) text-base font-semibold">
            {title}
          </h2>
          <p className="text-(--color-text-secondary) mt-0.5 text-sm">
            {description}
          </p>
          <button
            type="button"
            onClick={onAction}
            className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) mt-4 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
      />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 0 1 21.75 8.25Z"
      />
    </svg>
  );
}
